import {
  LoaderFunctionArgs,
  replace,
  ShouldRevalidateFunctionArgs,
  useRouteLoaderData,
} from "react-router";
import { match, P } from "ts-pattern";

import { Organization } from "./organization";
import { Invitation } from "../../features/user/invitation";
import { AuthService } from "../auth";
import { createApiClient } from "../api-client";
import { getIntent } from "../data-routing";

export type User = {
  id: string;
  email: string;
  email_verified: boolean;
  name: string;
  isFederated: boolean;
};

export async function fetchHubUserContext(accessToken: string): Promise<{
  orgaResponse: Array<Organization>;
  invitationResponse: Array<Invitation>;
}> {
  const apiClient = createApiClient(async () => accessToken);
  const [orgaResponse, invitationResponse] = await Promise.all([
    apiClient.getOrganizations(),
    apiClient.getInvitations(),
  ]);
  return { orgaResponse, invitationResponse };
}

export async function userStateLoader({ request }: LoaderFunctionArgs) {
  const currentPath = new URL(request.url).pathname;
  const { orgaResponse: organizations, invitationResponse: invitations } =
    await fetchHubUserContext(await AuthService.getTokenSilently());

  const hasInvitations = invitations.length > 0;
  const hasOrganizations = organizations.length > 0;

  return match({ hasInvitations, hasOrganizations, currentPath })
    .with(
      {
        hasInvitations: P.boolean,
        hasOrganizations: false,
        currentPath: P.not("/user/complete-profile"),
      },
      async () => replace("/user/complete-profile")
    )
    .with(
      {
        hasInvitations: true,
        hasOrganizations: true,
        currentPath: P.not("/user/accept-invitation"),
      },
      async () => replace("/user/accept-invitation")
    )
    .with(
      {
        hasInvitations: false,
        hasOrganizations: true,
        currentPath: P.union("/user/accept-invitation", "/user/complete-profile"),
      },
      async () => replace("/")
    )
    .otherwise(async () => {
      const user = await AuthService.user();
      return { user, organizations, invitations };
    });
}

export function shouldRevalidateUserState({
  json,
  currentUrl,
  currentParams,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const intent = getIntent(json);
  const currentPath = currentUrl.pathname;
  if (
    (currentPath === "/user/profile" && intent === "renameUser") ||
    (currentPath === `/${currentParams.orgaId}/settings` && intent === "renameOrganization") ||
    (currentPath === "/user/accept-invitation" && intent === "acceptInvitation") ||
    (currentPath === "/user/accept-invitation" && intent === "declineInvitation") ||
    (currentPath === "/user/complete-profile" && intent == null)
  ) {
    return defaultShouldRevalidate;
  }
  return false;
}

export function useUser(): {
  organizations: Array<Organization>;
  user?: User;
  invitations: Array<Invitation>;
} {
  const { user, organizations, invitations } = useRouteLoaderData("user-state");
  return { user, organizations, invitations };
}
